// @ts-check

/** @typedef {import ('./constants').SecureScoreProfileType} SecureScoreProfileType */

/**
 * Recommended OOB Defender template tags (must match DEFENDER_TEMPLATE_TAGS in templateUtils).
 * Kept as string literals so this module stays free of policy/template import cycles.
 */
const DEFENDER_TAGS = {
  ANTI_PHISHING_MOST_AGGRESSIVE: 'ANTI_PHISHING_MOST_AGGRESSIVE',
  ANTI_SPAM_INBOUND: 'ANTI_SPAM_INBOUND',
  ANTI_SPAM_OUTBOUND: 'ANTI_SPAM_OUTBOUND',
  ANTI_MALWARE: 'ANTI_MALWARE',
  SAFE_ATTACHMENTS: 'SAFE_ATTACHMENTS',
  SAFE_LINKS: 'SAFE_LINKS',
}

const ANTI_PHISH = {
  exoPolicyType: 'AntiPhishPolicy',
  defenderTemplateTag: DEFENDER_TAGS.ANTI_PHISHING_MOST_AGGRESSIVE,
}

const SAFE_LINKS = {
  exoPolicyType: 'SafeLinksPolicy',
  defenderTemplateTag: DEFENDER_TAGS.SAFE_LINKS,
}

const SAFE_ATTACHMENTS = {
  exoPolicyType: 'SafeAttachmentPolicy',
  defenderTemplateTag: DEFENDER_TAGS.SAFE_ATTACHMENTS,
}

const ANTI_MALWARE = {
  exoPolicyType: 'MalwareFilterPolicy',
  defenderTemplateTag: DEFENDER_TAGS.ANTI_MALWARE,
}

const ANTI_SPAM_INBOUND = {
  exoPolicyType: 'HostedContentFilterPolicy',
  defenderTemplateTag: DEFENDER_TAGS.ANTI_SPAM_INBOUND,
}

const ANTI_SPAM_OUTBOUND = {
  exoPolicyType: 'HostedOutboundSpamFilterPolicy',
  defenderTemplateTag: DEFENDER_TAGS.ANTI_SPAM_OUTBOUND,
}

/**
 * Checks with no deployable Defender policy template today (ATP org settings, mail-flow, connection filter).
 * Leave exoPolicyType null so Configure consumers do not invent false wiring.
 */
const UNSUPPORTED = {
  exoPolicyType: null,
  settings: null,
  defenderTemplateTag: null,
}

/**
 * Per MDO Secure Score Profile check: which Exchange Online Defender policy type and
 * settings the check evaluates, plus the OOB template tag for a future create-new path.
 *
 * @returns {Record<SecureScoreProfileType, object>}
 * WARNING: To deprecate the Secure Score Profile check, set the value to null.
 */
export const secureScoreProfiles = () => ({
  // Anti-phishing family — recommended create-new template is Most Aggressive (AP-12003)
  mdo_antiphishingpolicies: {
    ...ANTI_PHISH,
    // Policy-presence intent: deploy creates a custom anti-phish policy.
    settings: {},
  },
  mdo_phishthresholdlevel: {
    ...ANTI_PHISH,
    settings: { PhishThresholdLevel: { gte: 2 } },
  },
  mdo_targetedusersprotection: {
    ...ANTI_PHISH,
    // OOB Most Aggressive still has EnableTargetedUserProtection: false — edit path / Slice A must account for that.
    settings: { EnableTargetedUserProtection: true },
  },
  mdo_enabledomainstoprotect: {
    ...ANTI_PHISH,
    settings: {
      EnableOrganizationDomainsProtection: true,
      EnableTargetedDomainsProtection: true,
    },
  },
  mdo_enablemailboxintelligence: {
    ...ANTI_PHISH,
    settings: { EnableMailboxIntelligence: true },
  },
  mdo_mailboxintelligenceprotection: {
    ...ANTI_PHISH,
    settings: { EnableMailboxIntelligenceProtection: true },
  },
  mdo_targeteduserprotectionaction: {
    ...ANTI_PHISH,
    settings: { TargetedUserProtectionAction: 'Quarantine' },
  },
  mdo_targeteddomainprotectionaction: {
    ...ANTI_PHISH,
    settings: { TargetedDomainProtectionAction: 'Quarantine' },
  },
  mdo_mailboxintelligenceprotectionaction: {
    ...ANTI_PHISH,
    settings: { MailboxIntelligenceProtectionAction: 'Quarantine' },
  },
  mdo_similaruserssafetytips: {
    ...ANTI_PHISH,
    settings: { EnableSimilarUsersSafetyTips: true },
  },
  mdo_similardomainssafetytips: {
    ...ANTI_PHISH,
    settings: { EnableSimilarDomainsSafetyTips: true },
  },
  mdo_unusualcharacterssafetytips: {
    ...ANTI_PHISH,
    settings: { EnableUnusualCharactersSafetyTips: true },
  },

  // Safe Links
  mdo_safelinksforemail: {
    ...SAFE_LINKS,
    settings: { EnableSafeLinksForEmail: true },
  },
  mdo_safelinksforOfficeApps: {
    ...SAFE_LINKS,
    settings: { EnableSafeLinksForOffice: true },
  },

  // Safe Attachments
  mdo_safeattachments: {
    ...SAFE_ATTACHMENTS,
    settings: { Action: 'Block' },
  },
  mdo_safeattachmentpolicy: {
    ...SAFE_ATTACHMENTS,
    settings: { Enable: true },
  },

  // Anti-malware
  mdo_commonattachmentsfilter: {
    ...ANTI_MALWARE,
    settings: { EnableFileFilter: true },
  },
  mdo_zapmalware: {
    ...ANTI_MALWARE,
    settings: { ZapEnabled: true },
  },

  // Anti-spam inbound
  mdo_spamaction: {
    ...ANTI_SPAM_INBOUND,
    settings: { SpamAction: 'MoveToJmf' },
  },
  mdo_highconfidencespamaction: {
    ...ANTI_SPAM_INBOUND,
    settings: { HighConfidenceSpamAction: 'Quarantine' },
  },
  mdo_phisspamacation: {
    ...ANTI_SPAM_INBOUND,
    settings: { PhishSpamAction: 'Quarantine' },
  },
  mdo_highconfidencephishaction: {
    ...ANTI_SPAM_INBOUND,
    settings: { HighConfidencePhishAction: 'Quarantine' },
  },
  mdo_bulkspamaction: {
    ...ANTI_SPAM_INBOUND,
    settings: { BulkSpamAction: 'MoveToJmf' },
  },
  mdo_bulkthreshold: {
    ...ANTI_SPAM_INBOUND,
    settings: { BulkThreshold: { lte: 6 } },
  },
  mdo_quarantineretentionperiod: {
    ...ANTI_SPAM_INBOUND,
    settings: { QuarantineRetentionPeriod: 30 },
  },
  mdo_zapphish: {
    ...ANTI_SPAM_INBOUND,
    settings: { PhishZapEnabled: true },
  },
  mdo_zapspam: {
    ...ANTI_SPAM_INBOUND,
    settings: { SpamZapEnabled: true },
  },
  mdo_allowedsenderscombined: {
    ...ANTI_SPAM_INBOUND,
    settings: {
      AllowedSenders: [],
      AllowedSenderDomains: [],
    },
  },

  // Anti-spam outbound
  mdo_autoforwardingmode: {
    ...ANTI_SPAM_OUTBOUND,
    settings: { AutoForwardingMode: 'Off' },
  },
  mdo_thresholdreachedaction: {
    ...ANTI_SPAM_OUTBOUND,
    settings: { ActionWhenThresholdReached: 'BlockUser' },
  },
  mdo_recipientexternallimitperhour: {
    ...ANTI_SPAM_OUTBOUND,
    settings: { RecipientLimitExternalPerHour: 500 },
  },
  mdo_recipientinternallimitperhour: {
    ...ANTI_SPAM_OUTBOUND,
    settings: { RecipientLimitInternalPerHour: 1000 },
  },
  mdo_recipientlimitperday: {
    ...ANTI_SPAM_OUTBOUND,
    settings: { RecipientLimitPerDay: 1000 },
  },
  mdo_spam_notifications_only_for_admins: {
    ...ANTI_SPAM_OUTBOUND,
    // OOB outbound template has NotifyOutboundSpam: false — edit path may need to flip this.
    settings: { NotifyOutboundSpam: true },
  },

  // No Defender OOB / create type today
  mdo_atpprotection: { ...UNSUPPORTED },
  mdo_safedocuments: { ...UNSUPPORTED },
  mdo_blockmailforward: { ...UNSUPPORTED },
  // Connection filter uses a separate Configure path (connectionfilteripallowlist), not Defender templates
  mdo_connectionfilter: { ...UNSUPPORTED },
})
